import withRouter from "hoc/withRouter";
import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useGetPlans } from "hooks/useGetPlans";
import { Plan } from "interfaces/entities/plan.interface";
import PlanCard from "./PlanCard";
import PageTitle from "components/PageTitle/PageTitle";
import { useState } from "react";
import subscriptionService from "services/admin/subscription.service";
import { toast } from "react-toastify";
import { useGetSubscription } from "hooks/useGetSubscription";
import { toCurrencyFormat } from "helpers/utils";
import { queryClient } from "queryClient";

const PlansList = () => {
    const { data, isLoading, error } = useGetPlans(localStorage.getItem("orgID") || "");
    const { data: existingSubscription } = useGetSubscription(localStorage.getItem("orgID") || "");

    const [planToSubscribe, setPlanToSubscribe] = useState<Plan | null>(null);
    const [isAgreeWithPaymentPolicy, setIsAgreeWithPaymentPolicy] = useState(false);
    const [isOpenConfirmSubscription, setIsOpenConfirmSubscription] = useState(false);
    const [isOpenConfirmUnsubscribe, setIsOpenConfirmUnsubscribe] = useState(false);

    const subscribe = async () => {
        try {
            if (existingSubscription && existingSubscription?.planID !== planToSubscribe?.id) {
                // Смена тарифа
                if (!window.confirm('Вы уверены, что хотите сменить тариф?')) {
                    return;
                }
            }

            const windowWidth = window.outerWidth;
            const windowHeight = window.outerHeight;
            const { data: res } = await subscriptionService.post({
                planID: planToSubscribe?.id, window: {
                    width: windowWidth,
                    height: windowHeight
                }
            });

            if (!res) {
                toast.error('Ошибка при подписке на тариф!');
                return;
            }

            if (res.url) {
                window.location.href = `${process.env.REACT_APP_API_ADDRESS}${res.url}`;
            }
        } catch (error) {
            console.log(error);
            toast.error('Ошибка при подписке на тариф!');
            setPlanToSubscribe(null);
        } finally {
            setIsOpenConfirmSubscription(false);
        }
    }

    const unsubscribe = async () => {
        try {
            await subscriptionService.patch(existingSubscription?.id, {});
            toast.success(`Подписка отменена успешно!`);
            setPlanToSubscribe(null);
            queryClient.invalidateQueries({ queryKey: ['subscription', localStorage.getItem("orgID") || ""] });
        } catch (error) {
            console.log(error);
            toast.error('Ошибка при отписке от тарифа!');
        } finally {
            setIsOpenConfirmUnsubscribe(false);
        }
    }

    const showConfirmSubscription = (plan: Plan) => {
        setPlanToSubscribe(plan);
        setIsOpenConfirmSubscription(true);
    }

    const showConfirmUnsubscribe = () => {
        setIsOpenConfirmUnsubscribe(true);
    }

    function buildContent() {
        if (error) {
            return <div>{error.message}</div>
        }
        if (isLoading) {
            return <div className="text-center"><CircularProgress></CircularProgress></div>
        }
        if (data?.length === 0) {
            return <div>Тарифы отсутсвуют. Чтобы добавить тариф пожалуйста, нажмите на кнопку "Добавить" сверху</div>
        }
        if (data?.length && data?.length > 0) {
            return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data?.map((plan: Plan) => (
                    <PlanCard
                        key={plan.id}
                        plan={plan}
                        subscribe={() => showConfirmSubscription(plan)}
                        unsubscribe={() => showConfirmUnsubscribe()}
                        isSubscribed={existingSubscription && existingSubscription.planID === plan.id} />
                ))}
            </div>
        }
    }
    return (
        <div>
            <PageTitle>Список тарифов</PageTitle>
            {buildContent()}
            {/* Diaglog подтверждения подписки */}
            <Dialog open={isOpenConfirmSubscription} onClose={() => setIsOpenConfirmSubscription(false)}>
                <DialogContent>
                    <DialogTitle>Подтверждение подписки</DialogTitle>
                    <DialogContentText>
                        <div>
                            Я согласен на оформление подписки с оплатой <strong>{toCurrencyFormat(planToSubscribe?.price || 0, planToSubscribe?.currency || "KZT")}</strong> / месяц. Подписка продлевается автоматически.
                            Условие оплаты: <a href="https://hajjtravel.kz/payment-policy" target="_blank">Условие оплаты</a> также <a href="https://hajjtravel.kz/refund-policy" target="_blank">Политика возврата и отмены подписки
                            </a>
                        </div>
                        <div className="mt-4">
                            <Checkbox checked={isAgreeWithPaymentPolicy} onChange={(e) => setIsAgreeWithPaymentPolicy(e.target.checked)} />
                            Я согласен с условиями оплаты и политикой возврата и отмены подписки
                        </div>
                    </DialogContentText>
                    <DialogActions>
                        <Button onClick={() => {
                            setIsOpenConfirmSubscription(false)
                            setPlanToSubscribe(null)
                        }}>Отменить</Button>
                        <Button variant="contained" color="primary" onClick={subscribe} disabled={!isAgreeWithPaymentPolicy}>Оформить подписку</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>

            {/* Dialog подтверждения отмены подписки */}
            <Dialog open={isOpenConfirmUnsubscribe} onClose={() => setIsOpenConfirmUnsubscribe(false)}>
                <DialogContent>
                    <DialogTitle>Подтверждение отмены подписки</DialogTitle>
                    <DialogContentText>
                        <div>
                            Вы уверены, что хотите отменить подписку?
                            После отмены доступ к функциям будет доступен до конца оплаченного периода. Все ваши данные будут сохранены.
                        </div>
                    </DialogContentText>
                    <DialogActions>
                        <Button onClick={() => setIsOpenConfirmUnsubscribe(false)}>Отменить</Button>
                        <Button variant="contained" color="primary" onClick={() => unsubscribe()}>Отменить подписку</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default withRouter(PlansList);
